class PiBundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    // Bind handlers once to track them
    this.boundHandlers = {
      frequencyChange: this.handleFrequencyChange.bind(this),
      addProduct: this.handleAddProductClick.bind(this),
      removeProduct: this.handleRemoveProductClick.bind(this),
      buildButton: this.handleBuildButtonClick.bind(this),
      buildButtonModal: this.handleBuildButtonModalClick.bind(this),
      closeModal: this.handleCloseModalClick.bind(this),
      itemQuantity: this.handleItemQuantityChange.bind(this)
    };
    this._isChangingFrequency = false;
    this._isProcessing = false;
    this.init();
  }

  init() {
    // Check for delivery frequency select and set up change listener
    const deliveryFrequencySelects = this.querySelectorAll('.pi-bundle-delivery-frequency__select');
    deliveryFrequencySelects.forEach(select => {
      // Remove existing listener if any (using the bound handler)
      select.removeEventListener('change', this.boundHandlers.frequencyChange);

      // Check URL parameter and preselect if available
      const urlParams = new URLSearchParams(window.location.search);
      const frequencyParam = urlParams.get('frequency');
      if (frequencyParam) {
        select.value = decodeURIComponent(frequencyParam);
      }

      // Listen for frequency changes
      select.addEventListener('change', this.boundHandlers.frequencyChange);
    });

    // Remove and re-add click listeners to prevent duplicates
    this.removeEventListener('click', this.boundHandlers.addProduct);
    this.removeEventListener('click', this.boundHandlers.removeProduct);
    this.removeEventListener('click', this.boundHandlers.buildButton);
    this.removeEventListener('click', this.boundHandlers.buildButtonModal);
    this.removeEventListener('click', this.boundHandlers.closeModal);
    this.removeEventListener('click', this.boundHandlers.itemQuantity);

    // Listen for clicks on add product buttons
    this.addEventListener('click', this.boundHandlers.addProduct);
    
    // Listen for clicks on remove product buttons
    this.addEventListener('click', this.boundHandlers.removeProduct);
    
    // Listen for clicks on build button (checkout)
    this.addEventListener('click', this.boundHandlers.buildButton);
    
    // Listen for clicks on build button modal
    this.addEventListener('click', this.boundHandlers.buildButtonModal);
    
    // Listen for clicks on close modal button
    this.addEventListener('click', this.boundHandlers.closeModal);
    
    // Listen for clicks on item quantity buttons (increase/decrease)
    this.addEventListener('click', this.boundHandlers.itemQuantity);
  }

  handleFrequencyChange(event) {
    // Prevent multiple simultaneous frequency changes
    if (this._isChangingFrequency) {
      return;
    }
    this._isChangingFrequency = true;

    const selectedFrequency = event.target.value;
    if (!selectedFrequency) {
      this._isChangingFrequency = false;
      return;
    }

    // Update URL with frequency parameter without page reload
    const url = new URL(window.location);
    url.searchParams.set('frequency', encodeURIComponent(selectedFrequency));
    
    // Update URL without reloading page
    window.history.pushState({}, '', url);

    // Sync all frequency selects to have the same value (without triggering events)
    const allFrequencySelects = this.querySelectorAll('.pi-bundle-delivery-frequency__select');
    allFrequencySelects.forEach(select => {
      if (select !== event.target && select.value !== selectedFrequency) {
        // Temporarily remove listener, change value, re-add listener
        select.removeEventListener('change', this.boundHandlers.frequencyChange);
        select.value = selectedFrequency;
        // Re-add listener after a short delay to avoid triggering
        setTimeout(() => {
          select.addEventListener('change', this.boundHandlers.frequencyChange);
        }, 50);
      }
    });

    // Reset flag after a short delay
    setTimeout(() => {
      this._isChangingFrequency = false;
    }, 200);

    // Dispatch custom event for other scripts
    this.dispatchEvent(new CustomEvent('frequency:changed', {
      bubbles: true,
      detail: { frequency: selectedFrequency }
    }));
  }

  async handleAddProductClick(event) {
    const addButton = event.target.closest('.pi-bundle__add-product-button');
    if (!addButton) return;

    // Prevent multiple simultaneous operations
    if (this._isProcessing) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    // Get product data from button attributes
    const variantId = addButton.dataset.variantId;
    const quantity = 1; // Default quantity
    const sellingPlanId = this.getSelectedSellingPlanId(addButton);

    if (!variantId) {
      console.error('Variant ID not found');
      return;
    }

    // Add to cart
    try {
      this._isProcessing = true;
      await this.addToCart(variantId, quantity, sellingPlanId);
      // Rerender section after adding to cart
      await this.rerenderSection();
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      this._isProcessing = false;
    }
  }

  async handleRemoveProductClick(event) {
    const removeButton = event.target.closest('.pi-bundle__remove-product-button');
    if (!removeButton) return;

    event.preventDefault();
    event.stopPropagation();

    // Get variant ID from button attributes
    const variantId = removeButton.dataset.variantId;

    if (!variantId) {
      console.error('Variant ID not found');
      return;
    }

    // Remove from cart
    try {
      await this.removeFromCart(variantId);
      // Rerender section after removing from cart
      await this.rerenderSection();
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  }

  handleBuildButtonClick(event) {
    const buildButton = event.target.closest('.pi-bundle__build-button');
    if (!buildButton) return;

    event.preventDefault();
    event.stopPropagation();

    // Redirect to checkout
    window.location.href = '/checkout';
  }

  handleBuildButtonModalClick(event) {
    const buildButtonModal = event.target.closest('.pi-bundle__build-button-modal');
    if (!buildButtonModal) return;

    event.preventDefault();
    event.stopPropagation();

    // Get the target modal ID from data-target attribute
    const targetModalId = buildButtonModal.getAttribute('data-target');
    if (!targetModalId) {
      console.error('No data-target attribute found on modal button');
      return;
    }

    // Find the modal element
    const modal = document.querySelector(targetModalId);
    if (!modal) {
      console.error('Modal not found:', targetModalId);
      return;
    }

    // Show the modal by adding 'show' class and updating aria attributes
    modal.classList.add('show');
    modal.setAttribute('aria-hidden', 'false');
    modal.style.display = 'block';

    // Add modal backdrop if it doesn't exist
    let backdrop = document.querySelector('.modal-backdrop');
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = 'modal-backdrop fade show';
      document.body.appendChild(backdrop);
      document.body.classList.add('modal-open');
    }
  }

  handleCloseModalClick(event) {
    const closeButton = event.target.closest('.bundle-builder__column-inner__close');
    if (!closeButton) return;

    event.preventDefault();
    event.stopPropagation();

    // Find the modal (could be parent or sibling depending on structure)
    // Try to find by traversing up the DOM or by finding the modal with the checkout class
    const modal = closeButton.closest('#bundle-builder__column-checkout') || 
                  document.querySelector('#bundle-builder__column-checkout');
    
    if (!modal) {
      console.error('Modal not found for close button');
      return;
    }

    // Hide the modal by removing 'show' class and updating aria attributes
    modal.classList.remove('show');
    modal.setAttribute('aria-hidden', 'true');
    modal.style.display = 'none';

    // Remove modal backdrop
    const backdrop = document.querySelector('.modal-backdrop');
    if (backdrop) {
      backdrop.remove();
    }
    
    // Remove modal-open class from body
    document.body.classList.remove('modal-open');
  }

  async handleItemQuantityChange(event) {
    const removeButton = event.target.closest('.pi-bundle__item-remove-product-button');
    const addButton = event.target.closest('.pi-bundle__item-add-product-button');
    
    if (!removeButton && !addButton) return;

    event.preventDefault();
    event.stopPropagation();

    const button = removeButton || addButton;
    const variantId = button.dataset.variantId;
    const isIncrease = !!addButton;

    if (!variantId) {
      console.error('Variant ID not found');
      return;
    }

    try {
      await this.updateItemQuantity(variantId, isIncrease);
      await this.rerenderSection();
    } catch (error) {
      console.error('Error updating item quantity:', error);
    }
  }

  getSelectedSellingPlanId(button) {
    // Get the selected delivery frequency
    const deliveryFrequencySelect = this.querySelector('.pi-bundle-delivery-frequency__select');
    if (!deliveryFrequencySelect) return null;

    const selectedFrequency = deliveryFrequencySelect.value;
    if (!selectedFrequency) return null;

    // Get selling plans from button data attribute
    const sellingPlansData = button.dataset.productSellingPlans;
    if (!sellingPlansData) return null;

    try {
      const sellingPlans = JSON.parse(sellingPlansData);
      const matchingPlan = sellingPlans.find(plan => plan.name === selectedFrequency);
      return matchingPlan ? matchingPlan.id : null;
    } catch (error) {
      console.error('Error parsing selling plans:', error);
      return null;
    }
  }

  async addToCart(variantId, quantity, sellingPlanId) {
    const items = [{
      id: variantId,
      quantity: quantity,
      ...(sellingPlanId && { selling_plan: sellingPlanId })
    }];

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items })
    });

    if (!response.ok) {
      throw new Error('Failed to add to cart');
    }

    const data = await response.json();
    console.log('Added to cart:', data);

    // Get sections to render for cart drawer and cart count
    const cartDrawer = document.querySelector('cart-drawer');
    const sectionsToRender = cartDrawer ? cartDrawer.getSectionsToRender().map(section => section.id) : ['cart-icon-bubble'];
    const sectionsUrl = window.location.pathname;

    // Fetch sections using Shopify section rendering API
    const sectionsParam = sectionsToRender.join(',');
    const sectionsResponse = await fetch(`${sectionsUrl}?sections=${sectionsParam}`, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      }
    });
    
    if (sectionsResponse.ok) {
      const sectionsData = await sectionsResponse.json();
      
      // Get current cart data
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        // Combine cart data with sections
        cartData.sections = sectionsData;
        
        // Update cart drawer and cart count
        this.updateCartUI(cartData, sectionsToRender);
        
        // Publish cart update event
        if (typeof publish !== 'undefined' && window.PUB_SUB_EVENTS) {
          publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'pi-bundle-builder',
            productVariantId: variantId,
            cartData: cartData
          });
        }

        // Trigger icart app update
        this.triggerIcartUpdate();
      }
    }

    return data;
  }

  async removeFromCart(variantId) {
    // First, get the cart to find the line item key for this variant
    const cartResponse = await fetch('/cart.js');
    if (!cartResponse.ok) {
      throw new Error('Failed to fetch cart');
    }

    const cart = await cartResponse.json();
    
    // Find all line items with this variant ID (including different selling plans)
    const lineItemKeys = cart.items
      .filter(item => item.variant_id.toString() === variantId.toString())
      .map(item => item.key);

    if (lineItemKeys.length === 0) {
      console.warn('No items found with variant ID:', variantId);
      return;
    }

    // Remove all instances of this variant from cart
    const updates = {};
    lineItemKeys.forEach(key => {
      updates[key] = 0; // Set quantity to 0 to remove
    });

    // Get sections to render for cart drawer and cart count
    const cartDrawer = document.querySelector('cart-drawer');
    const sectionsToRender = cartDrawer ? cartDrawer.getSectionsToRender().map(section => section.id) : ['cart-icon-bubble'];
    const sectionsUrl = window.location.pathname;

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        updates,
        sections: sectionsToRender,
        sections_url: sectionsUrl
      })
    });

    if (!response.ok) {
      throw new Error('Failed to remove from cart');
    }

    const responseText = await response.text();
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      // If response is not JSON, fetch cart separately
      const cartDataResponse = await fetch('/cart.js');
      if (cartDataResponse.ok) {
        data = await cartDataResponse.json();
      } else {
        throw new Error('Failed to parse cart response');
      }
    }

    console.log('Removed from cart:', data);

    // Fetch sections using Shopify section rendering API
    const sectionsParam = sectionsToRender.join(',');
    const sectionsResponse = await fetch(`${sectionsUrl}?sections=${sectionsParam}`, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      }
    });
    
    if (sectionsResponse.ok) {
      const sectionsData = await sectionsResponse.json();
      // Combine cart data with sections
      data.sections = sectionsData;
      // Update cart drawer and cart count
      this.updateCartUI(data, sectionsToRender);
    }

    // Publish cart update event
    if (typeof publish !== 'undefined' && window.PUB_SUB_EVENTS) {
      publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'pi-bundle-builder',
        productVariantId: variantId,
        cartData: data
      });
    }

    // Trigger icart app update
    this.triggerIcartUpdate();

    return data;
  }

  async updateItemQuantity(variantId, isIncrease) {
    // Get the cart to find the line item
    const cartResponse = await fetch('/cart.js');
    if (!cartResponse.ok) {
      throw new Error('Failed to fetch cart');
    }

    const cart = await cartResponse.json();
    
    // Find the first line item with this variant ID that has a selling plan
    const lineItem = cart.items.find(item => 
      item.variant_id.toString() === variantId.toString() && 
      item.selling_plan_allocation
    );

    if (!lineItem) {
      console.warn('No item found with variant ID and selling plan:', variantId);
      return;
    }

    // Calculate new quantity
    let newQuantity = lineItem.quantity;
    if (isIncrease) {
      newQuantity += 1;
    } else {
      newQuantity -= 1;
      // Don't allow quantity below 1 (if 1, this will remove it completely)
      if (newQuantity < 1) {
        newQuantity = 0;
      }
    }

    // Get sections to render for cart drawer and cart count
    const cartDrawer = document.querySelector('cart-drawer');
    const sectionsToRender = cartDrawer ? cartDrawer.getSectionsToRender().map(section => section.id) : ['cart-icon-bubble'];
    const sectionsUrl = window.location.pathname;

    // Update the cart
    const updates = {
      [lineItem.key]: newQuantity
    };

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        updates,
        sections: sectionsToRender,
        sections_url: sectionsUrl
      })
    });

    if (!response.ok) {
      throw new Error('Failed to update cart');
    }

    const responseText = await response.text();
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      // If response is not JSON, fetch cart separately
      const cartDataResponse = await fetch('/cart.js');
      if (cartDataResponse.ok) {
        data = await cartDataResponse.json();
      } else {
        throw new Error('Failed to parse cart response');
      }
    }

    console.log('Updated cart quantity:', data);

    // Fetch sections using Shopify section rendering API
    const sectionsParam = sectionsToRender.join(',');
    const sectionsResponse = await fetch(`${sectionsUrl}?sections=${sectionsParam}`, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      }
    });
    
    if (sectionsResponse.ok) {
      const sectionsData = await sectionsResponse.json();
      // Combine cart data with sections
      data.sections = sectionsData;
      // Update cart drawer and cart count
      this.updateCartUI(data, sectionsToRender);
    }

    // Publish cart update event
    if (typeof publish !== 'undefined' && window.PUB_SUB_EVENTS) {
      publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'pi-bundle-builder',
        productVariantId: variantId,
        cartData: data
      });
    }

    // Trigger icart app update
    this.triggerIcartUpdate();

    return data;
  }

  updateCartUI(cartData, sectionsToRender) {
    // Update cart drawer if it exists (without opening it)
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartData.sections) {
      // Manually update cart drawer sections without opening
      // We don't use renderContents() because it automatically opens the drawer
      if (typeof cartDrawer.getSectionsToRender === 'function') {
        cartDrawer.getSectionsToRender().forEach((section) => {
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          
          if (sectionElement && cartData.sections[section.id]) {
            const html = new DOMParser().parseFromString(cartData.sections[section.id], 'text/html');
            const newContent = html.querySelector(section.selector || '.shopify-section');
            if (newContent) {
              sectionElement.innerHTML = newContent.innerHTML;
            }
          }
        });
      } else {
        // Fallback: update cart drawer sections directly
        if (cartData.sections['cart-drawer']) {
          const cartDrawerElement = document.getElementById('CartDrawer');
          if (cartDrawerElement) {
            const html = new DOMParser().parseFromString(cartData.sections['cart-drawer'], 'text/html');
            const newDrawerContent = html.querySelector('#CartDrawer') || html.querySelector('.drawer__inner');
            if (newDrawerContent) {
              const drawerInner = cartDrawerElement.querySelector('.drawer__inner');
              if (drawerInner) {
                drawerInner.innerHTML = newDrawerContent.innerHTML;
              }
            }
          }
        }
      }
      
      // Update cart drawer items if they exist
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawerItems && cartData.sections['cart-drawer']) {
        const html = new DOMParser().parseFromString(cartData.sections['cart-drawer'], 'text/html');
        const newDrawerItems = html.querySelector('cart-drawer-items');
        if (newDrawerItems) {
          cartDrawerItems.innerHTML = newDrawerItems.innerHTML;
        }
      }
    }

    // Update cart count bubble
    if (cartData.sections && cartData.sections['cart-icon-bubble']) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        const html = new DOMParser().parseFromString(cartData.sections['cart-icon-bubble'], 'text/html');
        const newCartIcon = html.querySelector('#cart-icon-bubble') || html.querySelector('.shopify-section');
        if (newCartIcon) {
          cartIconBubble.innerHTML = newCartIcon.innerHTML;
        }
      }
    }

    // Update cart drawer empty state
    if (cartDrawer) {
      const itemCount = cartData.item_count || 0;
      cartDrawer.classList.toggle('is-empty', itemCount === 0);
      const drawerInner = cartDrawer.querySelector('.drawer__inner');
      if (drawerInner) {
        drawerInner.classList.toggle('is-empty', itemCount === 0);
      }
    }
  }

  triggerIcartUpdate() {
    // Trigger icart app update
    // icart typically listens to cart changes via events or window events
    try {
      // Dispatch custom event for icart
      if (typeof window !== 'undefined') {
        // Dispatch cart:updated event (common Shopify app event)
        window.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        
        // Dispatch icart-specific event if available
        window.dispatchEvent(new CustomEvent('icart:update', { bubbles: true }));
        
        // Check if icart has a global update function
        if (typeof window.icart !== 'undefined' && typeof window.icart.update === 'function') {
          window.icart.update();
        }
        
        // Check for icart jQuery plugin (if jQuery is available)
        if (typeof window.jQuery !== 'undefined' && typeof window.jQuery.fn.icart === 'function') {
          window.jQuery(document).trigger('icart:update');
        }
      }
    } catch (error) {
      console.warn('Error triggering icart update:', error);
    }
  }

  async rerenderSection() {
    if (!this.sectionId) {
      console.error('Section ID not found');
      return;
    }

    try {
      // Use Shopify Section Rendering API - preserve frequency parameter if exists
      const urlParams = new URLSearchParams(window.location.search);
      const frequencyParam = urlParams.get('frequency');
      
      let url = `${window.location.pathname}?section_id=${this.sectionId}`;
      if (frequencyParam) {
        url += `&frequency=${encodeURIComponent(frequencyParam)}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch section');
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newElement = doc.querySelector('pi-bundle-builder');

      if (newElement) {
        // Update only dynamic parts instead of replacing everything
        this.updateDynamicParts(newElement);
        
        // Reinitialize event listeners
        this.init();
        
        // Dispatch custom event for other scripts that might need to react
        this.dispatchEvent(new CustomEvent('section:rerendered', {
          bubbles: true,
          detail: { sectionId: this.sectionId }
        }));
      }
    } catch (error) {
      console.error('Error rerendering section:', error);
    }
  }

  updateDynamicParts(newElement) {
    // 1. Update product card quantities and active states (preserve images)
    const productWrapper = this.querySelector('.bundle-builder-products__wrapper');
    if (productWrapper) {
      const productCards = productWrapper.querySelectorAll('.pi-bundle__product-wrapper[data-variant-id], .bundle-builder-products__product[data-variant-id]');
      productCards.forEach(card => {
        const variantId = card.dataset.variantId;
        // Try both selectors in case class names differ
        const newCard = newElement.querySelector(`.pi-bundle__product-wrapper[data-variant-id="${variantId}"], .bundle-builder-products__product[data-variant-id="${variantId}"]`);
        
        if (newCard) {
          // Update quantity badge
          const quantityBadge = card.querySelector('.bundle-builder-products__quantity');
          const newQuantityBadge = newCard.querySelector('.bundle-builder-products__quantity');
          if (quantityBadge && newQuantityBadge) {
            quantityBadge.textContent = newQuantityBadge.textContent;
            quantityBadge.className = newQuantityBadge.className;
          }

          // Update active states
          card.classList.toggle('__active', newCard.classList.contains('__active'));

          // Update remove button active state
          const removeBtn = card.querySelector('.pi-bundle__remove-product-button');
          const newRemoveBtn = newCard.querySelector('.pi-bundle__remove-product-button');
          if (removeBtn && newRemoveBtn) {
            removeBtn.className = newRemoveBtn.className;
          }

          // Update prices (regular and dynamic)
          const priceRegular = card.querySelector('.product-price__price_regular');
          const newPriceRegular = newCard.querySelector('.product-price__price_regular');
          if (priceRegular && newPriceRegular) {
            priceRegular.textContent = newPriceRegular.textContent;
          }

          const priceDynamic = card.querySelector('.product-price__price_dynamic');
          const newPriceDynamic = newCard.querySelector('.product-price__price_dynamic');
          if (priceDynamic && newPriceDynamic) {
            priceDynamic.textContent = newPriceDynamic.textContent;
            priceDynamic.setAttribute('data-price', newPriceDynamic.getAttribute('data-price') || '');
            priceDynamic.setAttribute('data-base-price', newPriceDynamic.getAttribute('data-base-price') || '');
          }
        }
      });
    }

    // 2. Update selected products list (replace entire section)
    const selectedProductsWrapper = this.querySelector('.bundle-builder__product-select-buttons');
    const newSelectedProductsWrapper = newElement.querySelector('.bundle-builder__product-select-buttons');
    if (selectedProductsWrapper && newSelectedProductsWrapper) {
      selectedProductsWrapper.innerHTML = newSelectedProductsWrapper.innerHTML;
    }

    // 3. Update bundle totals and savings
    const savingsElement = this.querySelector('.bundle-builder__savings');
    const newSavingsElement = newElement.querySelector('.bundle-builder__savings');
    if (savingsElement && newSavingsElement) {
      savingsElement.textContent = newSavingsElement.textContent;
    }

    const bundlePriceElement = this.querySelector('.bundle-builder__price');
    const newBundlePriceElement = newElement.querySelector('.bundle-builder__price');
    if (bundlePriceElement && newBundlePriceElement) {
      bundlePriceElement.textContent = newBundlePriceElement.textContent;
    }

    const discountPercentElement = this.querySelector('.bundle-builder-products__summary__discount');
    const newDiscountPercentElement = newElement.querySelector('.bundle-builder-products__summary__discount');
    if (discountPercentElement && newDiscountPercentElement) {
      discountPercentElement.textContent = newDiscountPercentElement.textContent;
    }

    // 4. Update header savings
    const headerSavingsElement = this.querySelector('.bundle-builder-products__header__savings-money');
    const newHeaderSavingsElement = newElement.querySelector('.bundle-builder-products__header__savings-money');
    if (headerSavingsElement && newHeaderSavingsElement) {
      headerSavingsElement.textContent = newHeaderSavingsElement.textContent;
    }

    // 5. Update discount badges active states
    const discountBadges = this.querySelectorAll('.bundle-builder-products__header__discount');
    const newDiscountBadges = newElement.querySelectorAll('.bundle-builder-products__header__discount');
    discountBadges.forEach((badge, index) => {
      if (newDiscountBadges[index]) {
        badge.className = newDiscountBadges[index].className;
      }
    });

    // 6. Update placeholder visibility
    for (let i = 1; i <= 5; i++) {
      const placeholder = this.querySelector(`.bundle-builder__selected-product__placeholder_${i}`);
      const newPlaceholder = newElement.querySelector(`.bundle-builder__selected-product__placeholder_${i}`);
      if (placeholder && newPlaceholder) {
        placeholder.style.display = newPlaceholder.style.display;
      }
    }
  }
}

// Register the custom element
customElements.define('pi-bundle-builder', PiBundleBuilder);

